import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "components/ui/card";
import { OPPORTUNITIES_PATH } from "lib/opportunitiesNav";
import { selectAuthUser, selectIsAuthenticated } from "store/reducer";

function getUserDisplayName(user) {
  const profile = user?.individualProfile || user?.corporateProfile || {};
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(" ");
  return (
    name ||
    profile.companyName ||
    user?.name ||
    user?.email?.split("@")?.[0] ||
    ""
  );
}

export function NewsletterSignupSection({
  className = "py-16 sm:py-20 bg-muted/30",
  showAuthenticatedLinks = true,
}) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectAuthUser);
  const displayName = getUserDisplayName(user);

  return (
    <section className={className}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
        <div className="mx-auto max-w-xl text-center">
          {isAuthenticated && displayName ? (
            <p className="mb-2 text-sm font-medium text-foreground">
              Welcome back, {displayName}
            </p>
          ) : null}
          <h2 className="mb-4 text-2xl font-bold">Sign up for updates</h2>
          <p className="mb-6 text-muted-foreground">
            Register to stay in the loop with the latest crowdfunding projects.
          </p>
          <form
            className="flex flex-col gap-3 sm:flex-row"
            onSubmit={(e) => e.preventDefault()}
          >
            <Input
              type="email"
              placeholder="Enter your email"
              className="flex-1"
              name="cta-email"
              autoComplete="email"
              defaultValue={isAuthenticated ? user?.email ?? "" : undefined}
            />
            <Button type="submit">Subscribe</Button>
          </form>
          {showAuthenticatedLinks && isAuthenticated ? (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button
                asChild
                variant="secondary"
                className="d-none sm:flex-initial"
              >
                <Link to={OPPORTUNITIES_PATH}>Browse opportunities</Link>
              </Button>
              <Button variant="outline" asChild className="sm:flex-initial">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function CTASection() {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
        <NewsletterSignupSection className="mb-16 bg-transparent py-0" />

        {/* Three CTAs */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="text-center">
            <CardHeader>
              <CardTitle>{isAuthenticated ? "Your investments" : "Join the crowd"}</CardTitle>
              <CardDescription>
                {isAuthenticated
                  ? "Track positions, returns, and activity from your dashboard and portfolio."
                  : "We've helped Canadian businesses distribute hundreds of thousands in equity. What are you waiting for?"}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              {isAuthenticated ? (
                <>
                  <Button asChild className="d-none">
                    <Link to="/portfolio">View portfolio</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/dashboard">Dashboard</Link>
                  </Button>
                </>
              ) : (
                <Button asChild>
                  <Link to="/sign-up">Get started</Link>
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <CardTitle>Learn more</CardTitle>
              <CardDescription>
                Fortify helps individual investors acquire small amounts of equity in private start-ups.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" asChild>
                <Link to="/how-it-works">How it works</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <CardTitle>Register your company</CardTitle>
              <CardDescription>
                Get distribution and raise funds for your private company.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" asChild>
                <Link to="/raise">Apply now</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-muted/50 rounded-2xl p-8 md:p-12">
          <h3 className="text-2xl font-bold mb-4">
            {isAuthenticated ? "Discover what's next" : "Get in early"}
          </h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            {isAuthenticated
              ? "New campaigns go live regularly—browse offerings and add to your portfolio."
              : "Ground floor investment opportunities in Canadian companies."}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              size="lg"
              asChild
              className="d-none"
            >
              <Link to={OPPORTUNITIES_PATH}>Browse opportunities</Link>
            </Button>
            {isAuthenticated ? (
              <Button size="lg" variant="outline" asChild className="d-none">
                <Link to="/portfolio">Portfolio</Link>
              </Button>
            ) : (
              <Button size="lg" variant="outline" asChild>
                <Link to="/sign-up">Create an account</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
